/**
 * Ceremonies WebAuthn cote navigateur. DEUX usages, a ne pas confondre :
 *
 *  1. SECOND FACTEUR DU COMPTE SERVEUR (`docs/ARCHITECTURE.md` §3) --
 *     `createWebAuthnCredential`/`getWebAuthnAssertion`. Seule la conversion
 *     JSON <-> binaire vit ici : la signature est produite par
 *     l'authentificateur et verifiee par le serveur via `@klavox/webauthn-core`.
 *     Aucun secret du coffre n'est implique : options et reponses sont
 *     publiques par nature.
 *
 *  2. DEVERROUILLAGE LOCAL DU COFFRE par l'extension `prf`
 *     (`createPasskeyWithPrf`/`evaluatePasskeyPrf`, cf. `passkey-unlock.ts`).
 *     La sortie PRF est un SECRET : elle scelle une copie de la `vaultKey` et
 *     ne quitte jamais l'appareil -- aucun serveur, aucun reseau n'intervient.
 *     Il n'y a donc PERSONNE pour verifier la signature de l'assertion, et
 *     c'est sans consequence : la securite ne vient pas de la signature mais du
 *     PRF lui-meme, qu'aucun logiciel ne peut recalculer (cle HMAC
 *     inextractible de l'authentificateur, evaluee seulement apres verification
 *     de l'utilisateur -- biometrie ou PIN).
 */
import { PASSKEY_PRF_OUTPUT_LENGTH, randomBytes } from '@klavox/crypto-core';

/** Options d'enregistrement telles que renvoyees par `POST /v1/accounts/webauthn/register/begin`. */
export interface CreationOptionsJSON {
  readonly challenge: string;
  readonly rp: { readonly id?: string; readonly name: string };
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly pubKeyCredParams: readonly { readonly type: 'public-key'; readonly alg: number }[];
  readonly timeout?: number;
  readonly excludeCredentials?: readonly DescriptorJSON[];
  readonly authenticatorSelection?: AuthenticatorSelectionCriteria;
  readonly attestation?: AttestationConveyancePreference;
}

/** Options d'authentification telles que renvoyees par `POST /v1/sessions/webauthn/begin`. */
export interface RequestOptionsJSON {
  readonly challenge: string;
  readonly rpId?: string;
  readonly timeout?: number;
  readonly allowCredentials?: readonly DescriptorJSON[];
  readonly userVerification?: UserVerificationRequirement;
}

interface DescriptorJSON {
  readonly id: string;
  readonly type: 'public-key';
  readonly transports?: readonly string[];
}

export interface RegistrationResponseJSON {
  readonly id: string;
  readonly rawId: string;
  readonly type: 'public-key';
  readonly response: { readonly clientDataJSON: string; readonly attestationObject: string; readonly transports?: string[] };
  readonly authenticatorAttachment?: string;
  readonly clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

export interface AuthenticationResponseJSON {
  readonly id: string;
  readonly rawId: string;
  readonly type: 'public-key';
  readonly response: {
    readonly clientDataJSON: string;
    readonly authenticatorData: string;
    readonly signature: string;
    readonly userHandle?: string;
  };
  readonly authenticatorAttachment?: string;
  readonly clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

/** Ceremonie annulee par l'utilisateur, expiree, ou authentificateur refuse -- message affichable. */
export class WebAuthnUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebAuthnUnavailableError';
  }
}

export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && navigator.credentials !== undefined;
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/gu, '+').replace(/_/gu, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function bytesToBase64Url(value: ArrayBuffer | ArrayBufferView): string {
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
}

function toDescriptor(descriptor: DescriptorJSON): PublicKeyCredentialDescriptor {
  return {
    id: base64UrlToBytes(descriptor.id),
    type: descriptor.type,
    ...(descriptor.transports !== undefined ? { transports: [...descriptor.transports] as AuthenticatorTransport[] } : {}),
  };
}

/** Traduit les rejets DOM en message utilisateur, sans jamais laisser croire a un succes. */
function describeCeremonyError(cause: unknown): WebAuthnUnavailableError {
  const name = cause instanceof Error ? cause.name : '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return new WebAuthnUnavailableError('Operation annulee ou delai depasse sur la cle de securite.');
  }
  if (name === 'InvalidStateError') {
    return new WebAuthnUnavailableError('Cette cle de securite est deja enregistree sur ce compte.');
  }
  if (name === 'SecurityError') {
    return new WebAuthnUnavailableError('Domaine non autorise pour ce serveur (origine WebAuthn du serveur a verifier).');
  }
  return new WebAuthnUnavailableError('Cle de securite indisponible sur cet appareil.');
}

function requirePublicKeyCredential(credential: Credential | null): PublicKeyCredential {
  if (credential === null || credential.type !== 'public-key') {
    throw new WebAuthnUnavailableError('Aucune reponse de la cle de securite.');
  }
  return credential as PublicKeyCredential;
}

/** Enregistre un nouvel authentificateur (`navigator.credentials.create`). */
export async function createWebAuthnCredential(
  options: CreationOptionsJSON,
  container: CredentialsContainer = navigator.credentials,
): Promise<RegistrationResponseJSON> {
  let credential: Credential | null;
  try {
    credential = await container.create({
      publicKey: {
        challenge: base64UrlToBytes(options.challenge),
        rp: options.rp,
        user: { id: base64UrlToBytes(options.user.id), name: options.user.name, displayName: options.user.displayName },
        pubKeyCredParams: [...options.pubKeyCredParams],
        timeout: options.timeout,
        excludeCredentials: options.excludeCredentials?.map(toDescriptor),
        authenticatorSelection: options.authenticatorSelection,
        attestation: options.attestation,
      },
    });
  } catch (cause) {
    throw describeCeremonyError(cause);
  }
  const publicKey = requirePublicKeyCredential(credential);
  const response = publicKey.response as AuthenticatorAttestationResponse;
  const transports = typeof response.getTransports === 'function' ? response.getTransports() : undefined;
  return {
    id: publicKey.id,
    rawId: bytesToBase64Url(publicKey.rawId),
    type: 'public-key',
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      attestationObject: bytesToBase64Url(response.attestationObject),
      ...(transports !== undefined ? { transports } : {}),
    },
    ...(publicKey.authenticatorAttachment ? { authenticatorAttachment: publicKey.authenticatorAttachment } : {}),
    clientExtensionResults: publicKey.getClientExtensionResults(),
  };
}

/** Produit l'assertion du second facteur (`navigator.credentials.get`). */
export async function getWebAuthnAssertion(
  options: RequestOptionsJSON,
  container: CredentialsContainer = navigator.credentials,
): Promise<AuthenticationResponseJSON> {
  let credential: Credential | null;
  try {
    credential = await container.get({
      publicKey: {
        challenge: base64UrlToBytes(options.challenge),
        rpId: options.rpId,
        timeout: options.timeout,
        allowCredentials: options.allowCredentials?.map(toDescriptor),
        userVerification: options.userVerification,
      },
    });
  } catch (cause) {
    throw describeCeremonyError(cause);
  }
  const publicKey = requirePublicKeyCredential(credential);
  const response = publicKey.response as AuthenticatorAssertionResponse;
  return {
    id: publicKey.id,
    rawId: bytesToBase64Url(publicKey.rawId),
    type: 'public-key',
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      authenticatorData: bytesToBase64Url(response.authenticatorData),
      signature: bytesToBase64Url(response.signature),
      ...(response.userHandle !== null ? { userHandle: bytesToBase64Url(response.userHandle) } : {}),
    },
    ...(publicKey.authenticatorAttachment ? { authenticatorAttachment: publicKey.authenticatorAttachment } : {}),
    clientExtensionResults: publicKey.getClientExtensionResults(),
  };
}

// --- Deverrouillage local par PRF (usage 2 du bandeau) ----------------------

/**
 * Un authentificateur sans extension `prf` ne peut pas deverrouiller le coffre.
 * Distincte de {@link WebAuthnUnavailableError} : ce n'est ni une annulation ni
 * une panne, c'est une INCAPACITE de l'appareil -- l'IHM doit le dire ainsi.
 */
export class PasskeyPrfUnsupportedError extends Error {
  constructor(message = 'Cet authentificateur ne sait pas deriver de cle (extension WebAuthn « prf » absente).') {
    super(message);
    this.name = 'PasskeyPrfUnsupportedError';
  }
}

/**
 * Defi local. Aucun serveur ne le verifiera (cf. bandeau, usage 2) : il est
 * quand meme tire du CSPRNG, parce qu'un defi constant ferait d'une assertion
 * capturee une valeur rejouable -- et parce que l'authentificateur le signe.
 */
function localChallenge(): Uint8Array<ArrayBuffer> {
  return sansPartage(randomBytes(32));
}

/**
 * Recopie dans un tampon NON partage : `BufferSource` exclut `SharedArrayBuffer`,
 * et le coeur crypto rend des `Uint8Array` sans garantie sur leur tampon.
 */
function sansPartage(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(bytes);
}

/** Lit `prf.results.first` d'une reponse d'extension, s'il est present et de la bonne taille. */
function readPrfFirst(results: AuthenticationExtensionsClientOutputs): Uint8Array | null {
  const first = results.prf?.results?.first;
  if (first === undefined) return null;
  const bytes = first instanceof ArrayBuffer ? new Uint8Array(first) : new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
  // Copie : le tampon de l'extension appartient au navigateur.
  return bytes.length === PASSKEY_PRF_OUTPUT_LENGTH ? Uint8Array.from(bytes) : null;
}

export interface CreatePasskeyWithPrfInput {
  /** Nom affiche par le selecteur de passkey de l'OS. */
  readonly rpName: string;
  /** Identifiant de compte cote authentificateur (non secret : un identifiant local suffit). */
  readonly userId: Uint8Array;
  /** Libelle du compte dans le selecteur de l'OS (l'e-mail du coffre). */
  readonly userName: string;
  /** Sel d'evaluation PRF, un par passkey (`generatePasskeyPrfSalt`). */
  readonly salt: Uint8Array;
  /** Passkeys de deverrouillage deja enrolees : on evite un doublon sur le meme authentificateur. */
  readonly excludeCredentialIds?: readonly string[];
}

export interface CreatedPasskeyPrf {
  /** Identifiant de la passkey creee, en base64url (donnee publique). */
  readonly credentialId: string;
  /** Sortie PRF de 32 octets. SECRET : a adopter immediatement dans une `SecretKey`. */
  readonly prfOutput: Uint8Array;
}

/**
 * Cree une passkey de deverrouillage et obtient sa sortie PRF.
 *
 * Deux ceremonies au pire, une au mieux : certains navigateurs evaluent deja le
 * PRF a la creation (`prf.eval`), les autres exigent une authentification
 * ensuite -- on enchaine alors un `get()`, comme le fait Bitwarden. La passkey
 * cree reste sur l'authentificateur meme si le PRF echoue : l'appelant doit le
 * DIRE a l'utilisateur plutot que de laisser une passkey fantome sans
 * explication.
 *
 * @throws {PasskeyPrfUnsupportedError} authentificateur sans `prf` exploitable
 * @throws {WebAuthnUnavailableError} ceremonie annulee, expiree ou impossible
 */
export async function createPasskeyWithPrf(
  input: CreatePasskeyWithPrfInput,
  container: CredentialsContainer = navigator.credentials,
): Promise<CreatedPasskeyPrf> {
  let credential: Credential | null;
  try {
    credential = await container.create({
      publicKey: {
        challenge: localChallenge(),
        rp: { name: input.rpName },
        user: { id: sansPartage(input.userId), name: input.userName, displayName: input.userName },
        // ES256 puis RS256 : couvre les authentificateurs de plateforme et les cles materielles.
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        // Verification de l'utilisateur EXIGEE : c'est elle qui fait la biometrie
        // (Touch ID, Windows Hello) ou le PIN -- sans elle, la simple presence de
        // l'authentificateur ouvrirait le coffre.
        authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
        attestation: 'none',
        excludeCredentials: (input.excludeCredentialIds ?? []).map((id) => ({ id: base64UrlToBytes(id), type: 'public-key' })),
        extensions: { prf: { eval: { first: sansPartage(input.salt) } } },
      },
    });
  } catch (cause) {
    throw describeCeremonyError(cause);
  }
  const publicKey = requirePublicKeyCredential(credential);
  const credentialId = bytesToBase64Url(publicKey.rawId);
  const results = publicKey.getClientExtensionResults();
  const direct = readPrfFirst(results);
  if (direct !== null) return { credentialId, prfOutput: direct };
  // Pas d'evaluation a la creation : on la demande par une authentification.
  // On n'exige PAS `prf.enabled` -- des navigateurs l'omettent alors que le PRF
  // fonctionne ; si l'evaluation echoue vraiment, `evaluatePasskeyPrf` le dira.
  const prfOutput = await evaluatePasskeyPrf({ credentialIds: [credentialId], salts: { [credentialId]: input.salt } }, container);
  return { credentialId: prfOutput.credentialId, prfOutput: prfOutput.prfOutput };
}

export interface EvaluatePasskeyPrfInput {
  /** Passkeys acceptees (base64url). L'utilisateur choisit celle qu'il a sous la main. */
  readonly credentialIds: readonly string[];
  /** Sel par passkey : chaque enrolement a le sien. */
  readonly salts: Readonly<Record<string, Uint8Array>>;
}

export interface EvaluatedPasskeyPrf {
  /** La passkey effectivement utilisee (peut ne pas etre la premiere de la liste). */
  readonly credentialId: string;
  /** Sortie PRF de 32 octets. SECRET. */
  readonly prfOutput: Uint8Array;
}

/**
 * Demande a l'authentificateur d'evaluer le PRF pour l'une des passkeys
 * enrolees. Avec plusieurs passkeys, `evalByCredential` fournit le bon sel a
 * chacune ; avec une seule, `eval` suffit et reste compris des navigateurs qui
 * ignorent `evalByCredential`.
 *
 * @throws {PasskeyPrfUnsupportedError} l'authentificateur n'a rien evalue
 * @throws {WebAuthnUnavailableError} ceremonie annulee, expiree ou impossible
 */
export async function evaluatePasskeyPrf(
  input: EvaluatePasskeyPrfInput,
  container: CredentialsContainer = navigator.credentials,
): Promise<EvaluatedPasskeyPrf> {
  const ids = input.credentialIds;
  if (ids.length === 0) throw new PasskeyPrfUnsupportedError('Aucune passkey de deverrouillage enregistree sur cet appareil.');
  const seul = ids.length === 1 ? input.salts[ids[0]!] : undefined;
  const prf: AuthenticationExtensionsPRFInputs =
    seul !== undefined
      ? { eval: { first: sansPartage(seul) } }
      : { evalByCredential: Object.fromEntries(ids.map((id) => [id, { first: sansPartage(input.salts[id]!) }])) };
  let credential: Credential | null;
  try {
    credential = await container.get({
      publicKey: {
        challenge: localChallenge(),
        allowCredentials: ids.map((id) => ({ id: base64UrlToBytes(id), type: 'public-key' as const })),
        userVerification: 'required',
        extensions: { prf },
      },
    });
  } catch (cause) {
    throw describeCeremonyError(cause);
  }
  const publicKey = requirePublicKeyCredential(credential);
  const prfOutput = readPrfFirst(publicKey.getClientExtensionResults());
  if (prfOutput === null) throw new PasskeyPrfUnsupportedError();
  return { credentialId: bytesToBase64Url(publicKey.rawId), prfOutput };
}
